/**
 * Echo Hunt -- Settings tab: the seed-word list filters + the "generate 10 fakes" review tool (P6).
 *
 * Two jobs, both §16.2 P6 deliverables:
 *
 *  1. WORD-LIST FILTERS. The project already ships `WordConcreteness_Sorted_Tagged.json`, which its
 *     author hand-annotated with `BadResolve` / `BadCrawl` / `NSFW` -- but ONLY above `Conc.M >= 4.48`.
 *     Lowering the concreteness floor therefore silently escapes that annotation, so this panel warns
 *     LOUDLY (not a subtle note) when the floor goes below it.
 *
 *  2. THE DEV TOOL. "Generate 10 fakes" shows seeds -> output -> violations for each candidate, for
 *     the manual leakage review §9.5 calls for. It uses the live backend and the real corpus
 *     distributions, because the point is to look at real model output, not at a mock.
 */
import React, {useState} from "react";
import {observer_mgl} from "mobx-graphlink";
import {Button, CheckBox, Column, Row, Spinner, Text} from "react-vcomponents";
import {ProgressSpinner} from "../../../Utils/ReactComponents/ProgressSpinner.js";
import {InfoButton} from "web-vcore";
import {SpinnerForPercent} from "../../../Utils/ReactComponents/SpinnerForPercent.js";
import {GetMyEchoProfile} from "../../../EchoHunt/Profiles_Live.js";
import {
	GenerateFakes_Live,
	GetFakeDistributions,
	GetUsableCorpusRows,
	GetWordlistSettings,
	IsSeedWordlistReady,
	SetWordlistSettings_Live,
} from "../../../EchoHunt/FakeGenerator_Live.js";
import {GenerateFakeResult} from "../../../EchoHunt/FakeGenerator.js";
import {
	DefaultWordlistSettings,
	WORDLIST_DEFAULT_CONCRETENESS_MIN,
	WORDLIST_DEFAULT_PERCENT_KNOWN_MIN,
	WORDLIST_KNOWN_TAGS,
	WORDLIST_TAG_COVERAGE_CONCRETENESS_MIN,
} from "../../../EchoHunt/Wordlist.js";
import {GetSeedWordlist} from "../../../EchoHunt/Wordlist_Live.js";
import {BulletEventType_values} from "../../../EchoHunt/Types.js";
import {CheckBullet} from "../../../EchoHunt/Normalize.js";
import {GetCheckOptionsFromProfile} from "../../../EchoHunt/Bullets_Live.js";

export const WordlistPanel = observer_mgl(()=>{
	const profile = GetMyEchoProfile();
	if (profile == null) {
		return <Text style={{opacity: .7}}>Create an Echo Hunt profile (above) to configure the seed-word list.</Text>;
	}

	const settings = GetWordlistSettings();
	const result = GetSeedWordlist(profile);

	// A change is written straight through, like the per-role model rows: these are cheap settings and a
	// separate Save button would leave the preview below out of sync with the stored values.
	const change = (updates: Partial<typeof settings>)=>{
		SetWordlistSettings_Live(profile.id, {...settings, ...updates}).catch(ex=>console.error("Failed to save wordlist settings:", ex));
	};
	const toggleTag = (tag: string, enabled: boolean)=>{
		const next = enabled ? [...settings.excludedTags.filter(a=>a != tag), tag] : settings.excludedTags.filter(a=>a != tag);
		change({excludedTags: next});
	};

	const belowCoverage = settings.concretenessMin < WORDLIST_TAG_COVERAGE_CONCRETENESS_MIN;

	return (
		<>
			<Row>
				<Text style={{fontWeight: "bold"}}>Seed words</Text>
				<InfoButton ml={5} text={[
					"The fake generator is given 1-2 random CONCRETE words as CONCEPTS to build a situation around (§5.2).",
					"It may never use the words themselves -- only the idea behind them.",
					"",
					"Source: Brysbaert, Warriner & Kuperman (2014), 40k English lemmas rated for concreteness.",
					"This project's copy has been hand-annotated with three tags:",
					"  BadResolve    -- hard or slow to visualize (ambiguous variant, or too fringe a word)",
					"  BadCrawl      -- annoying to put in a scene (too large, or part of a person/thing)",
					"  NSFW          -- self-explanatory; also covers things commonly considered unpleasant",
					"",
					`The author's cutoffs: Conc.M >= ${WORDLIST_DEFAULT_CONCRETENESS_MIN}, Percent_known >= ${WORDLIST_DEFAULT_PERCENT_KNOWN_MIN}`,
					"which yields 4763 words, or 1596 once all three tags are excluded.",
				].join("\n")}/>
			</Row>

			<Row mt={8} center style={{gap: 10}}>
				<Text style={{width: 150}}>Min. concreteness:</Text>
				<Spinner min={1} max={5} step={.01} value={settings.concretenessMin}
					onChange={val=>change({concretenessMin: val})}/>
				<Text ml={10} style={{width: 150}}>Min. percent known:</Text>
				<SpinnerForPercent min={0} value={settings.percentKnownMin}
					onChange={val=>change({percentKnownMin: val})}/>
				<Button ml={10} text="Reset to author's cutoffs" onClick={()=>change(DefaultWordlistSettings())}/>
			</Row>

			{/* the loud warning (§ user note): the manual tags only cover Conc.M >= 4.48 */}
			{belowCoverage &&
				<Column mt={8} style={{padding: 8, borderRadius: 4, background: "rgba(90,55,0,.75)", border: "1px solid orange"}}>
					<Text style={{fontWeight: "bold", color: "orange"}}>
						{"WARNING: words below Conc.M " + WORDLIST_TAG_COVERAGE_CONCRETENESS_MIN + " are UNTAGGED."}
					</Text>
					<Text style={{marginTop: 4}}>
						{`The BadResolve / BadCrawl / NSFW annotation was only done for entries with Conc.M >= ${WORDLIST_TAG_COVERAGE_CONCRETENESS_MIN}.`}
					</Text>
					<Text style={{marginTop: 2}}>
						{"Your floor is " + settings.concretenessMin + ", so part of the set you just included has never been reviewed:"
							+ " slow-to-visualize and unpleasant words WILL get through, and excluding those tags no longer means what it says."}
					</Text>
					<Text style={{marginTop: 2, opacity: .85}}>
						{"Rather than lowering the floor, prefer widening `Percent_known` or accepting fewer seeds."}
					</Text>
				</Column>}

			<Row mt={10} center style={{gap: 10, flexWrap: "wrap"}}>
				<Text>Exclude tags:</Text>
				{WORDLIST_KNOWN_TAGS.map(tag=>
					<CheckBox key={tag} text={tag} value={settings.excludedTags.includes(tag)}
						onChange={val=>toggleTag(tag, val)}/>,
				)}
				<CheckBox ml={10} text="Merge space/hyphen variants" value={settings.mergeDuplicates}
					onChange={val=>change({mergeDuplicates: val})}/>
			</Row>

			{/* how many seed concepts one fake is built around: the §5.2 [EXPERIMENT] knob */}
			<Row mt={10} center style={{gap: 8, flexWrap: "wrap"}}>
				<Text>Seed concepts per fake:</Text>
				<Text ml={4} style={{opacity: .85}}>min</Text>
				<Spinner min={1} max={6} step={1} value={settings.seedConceptCountMin} onChange={val=>change({
					seedConceptCountMin: val,
					// keep the range ordered rather than silently sorting at sample time
					seedConceptCountMax: Math.max(val, settings.seedConceptCountMax),
				})}/>
				<Text ml={4} style={{opacity: .85}}>max</Text>
				<Spinner min={1} max={6} step={1} value={settings.seedConceptCountMax} onChange={val=>change({
					seedConceptCountMax: val,
					seedConceptCountMin: Math.min(val, settings.seedConceptCountMin),
				})}/>
				<InfoButton ml={5} text={[
					"How many random CONCEPTS each generated fake must build its event around (§5.2).",
					"The count is drawn uniformly from this range, and that many DISTINCT seed words are drawn.",
					"",
					"This is an experiment knob. The design originally matched the count to the corpus's",
					"oddityCount distribution (so fakes had as many out-of-place objects as real bullets),",
					"but that measure clusters at 0-1 in practice, which pinned every fake to one concept.",
					"It is now set here instead; the distribution-matched path is kept commented out in",
					"`FakeGenerator.ts` so the two can be compared.",
					"",
					"Try 2..2 to force two concepts into every fake, or 1..3 to see how a heavier load reads.",
					"Watch for the failure mode the design doc warns about: forcing 3+ unrelated domains into",
					"one short event tends to read as contrived rather than dreamlike.",
				].join("\n")}/>
				<Text ml={6} style={{opacity: .6, fontSize: 11}}>
					{`fakes get ${settings.seedConceptCountMin == settings.seedConceptCountMax
						? `${settings.seedConceptCountMin}`
						: `${settings.seedConceptCountMin}-${settings.seedConceptCountMax}`} concept(s)`}
				</Text>
			</Row>

			{/* the supply check for the seed list */}
			<Column mt={10} style={{gap: 2}}>
				{result == null
					? <Row center style={{gap: 6}}><ProgressSpinner/><Text>Loading the word list…</Text></Row>
					: <>
						<Text>
							{`Usable seed words: ${result.words.length}  (from ${result.stats.totalEntries} entries / ${result.stats.totalGroups} words)`}
						</Text>
						<Text style={{opacity: .8}}>
							{`excluded: ${result.stats.excludedByCutoffs} by cutoffs, ${result.stats.excludedByTags} by tags, ${result.stats.excludedByWords} banned/sleep words`}
						</Text>
						{result.words.length == 0 &&
							<Text style={{color: "red"}}>{"No seed words survive these settings -- fake generation cannot run."}</Text>}
					{result.warnings.map((warning, i)=>
							<Text key={i} style={{color: "orange", fontSize: 12}}>{warning}</Text>,
						)}
						<Text style={{opacity: .6, fontSize: 11, marginTop: 4}}>
							{`sample: ${result.words.slice(0, 20).join(", ")}`}
						</Text>
					</>}
			</Column>
		</>
	);
});

export const FakeGeneratorDevPanel = observer_mgl(()=>{
	const profile = GetMyEchoProfile();
	const [running, setRunning] = useState(false);
	const [results, setResults] = useState<GenerateFakeResult[]>([]);
	const [error, setError] = useState<string|n>(null);

	const usableCorpusCount = GetUsableCorpusRows().length;
	const distributions = GetFakeDistributions();
	const ready = profile != null && IsSeedWordlistReady();

	if (profile == null) {
		return <Text style={{opacity: .7}}>Create an Echo Hunt profile (above) to use the fake generator.</Text>;
	}

	return (
		<>
			<Row>
				<Text style={{fontWeight: "bold"}}>Fake generator (dev review)</Text>
				<InfoButton ml={5} text={[
					"Generates 10 independent fakes with the live backend, so you can eyeball them for leaks",
					"(§9.5): near-synonyms of the seed words, abstract phrasing, recognizable LLM voice.",
					"",
					"Each fake is one call that sees ONLY its seed concepts, a target event type and a length band",
					"-- no story, no world, no other bullets, no corpus (§5.1).",
					"",
					"The seed words are banned from the output: if a reply uses one, it is rejected and regenerated.",
					"Every candidate (including rejected ones) is shown below with its rejection reason.",
					"",
					"Event types are drawn from your own corpus's distribution (§5.3), so run the Corpus index first",
					"-- otherwise the fallbacks below are used.",
					"The number of seed concepts per fake is set by the 'Seed concepts per fake' range above.",
				].join("\n")}/>
			</Row>

			<Column mt={6} style={{gap: 2}}>
				<Text style={{opacity: .8}}>
					{`Corpus: ${usableCorpusCount} usable bullet(s)  |  event types: ${BulletEventType_values.filter(a=>(distributions.byEventType[a] ?? 0) > 0).map(a=>`${a} ${distributions.byEventType[a]}`).join(", ") || "(none yet)"}`}
				</Text>
				<Text style={{opacity: .55, fontSize: 11}}>
					{`corpus oddity counts (diagnostic only -- no longer drives the seed count): ${Object.entries(distributions.byOddityCount).map(([value, count])=>`${value}:${count}`).join(", ") || "(none yet)"}`}
				</Text>
			</Column>

			<Row mt={8} center style={{gap: 8}}>
				<Button text={running ? "Generating…" : "Generate 10 fakes"} enabled={ready && !running} onClick={async()=>{
					setRunning(true);
					setResults([]);
					setError(null);
					try {
						const generated = await GenerateFakes_Live({count: 10, rng: Math.random});
						setResults(generated);
					} catch (ex) {
						setError(`${ex}`);
					}
					setRunning(false);
				}}/>
				{running && <ProgressSpinner/>}
				{!ready && <Text style={{color: "orange"}}>No usable seed words (see the seed-word filters above).</Text>}
			</Row>
			{error != null && <Text mt={6} style={{color: "red"}}>{error}</Text>}

			{results.length > 0 &&
				<Column mt={8} style={{gap: 6}}>
					{results.map((result, i)=><FakeResultRow key={i} result={result}/>)}
				</Column>}
		</>
	);
});

const FakeResultRow = observer_mgl((props: {result: GenerateFakeResult})=>{
	const {result} = props;
	const opts = GetCheckOptionsFromProfile();
	const accepted = result.bullet != null;

	return (
		<Column style={{padding: 6, borderRadius: 4, background: accepted ? "rgba(20,45,20,.6)" : "rgba(60,20,20,.6)"}}>
			<Row center style={{gap: 8, flexWrap: "wrap"}}>
				<Text style={{fontSize: 11, color: "lightgreen"}}>{result.eventType}</Text>
				<Text style={{fontSize: 11, color: "violet"}}>{`seed: ${result.seed.words.join(" + ")} (${result.seed.conceptCount} concept${result.seed.conceptCount == 1 ? "" : "s"})`}</Text>
				<Text style={{fontSize: 11, opacity: .6}}>{`target: ${result.lengthBand.minWords}-${result.lengthBand.maxWords} words`}</Text>
				{!accepted && <Text style={{fontSize: 11, color: "orange"}}>{`rejected: ${result.lastRejectReason ?? "unknown"}`}</Text>}
			</Row>

			{result.candidates.map((candidate, i)=>{
				const violations = candidate.text.length > 0 ? CheckBullet(candidate.text, opts) : [];
				return (
					<Column key={i} mt={4} style={{paddingLeft: 6, borderLeft: "2px solid rgba(255,255,255,.15)"}}>
						<Row center style={{gap: 8}}>
							<Text style={{fontSize: 11, opacity: .6}}>{`attempt ${i + 1}`}</Text>
							{candidate.rejected != null && <Text style={{fontSize: 11, color: "orange"}}>{candidate.rejected}</Text>}
							{candidate.rejected == null && <Text style={{fontSize: 11, color: "lightgreen"}}>accepted</Text>}
						</Row>
						<Text style={{fontSize: 13, marginTop: 2}}>{candidate.text.length > 0 ? candidate.text : "(empty)"}</Text>
						{violations.length > 0 &&
							<Text style={{fontSize: 11, color: "orange", marginTop: 2}}>
								{`would fail the §7 check: ${violations.map(a=>a.message).join(" | ")}`}
							</Text>}
					</Column>
				);
			})}
		</Column>
	);
});
